import type { Voice } from 'ml-text-service-client';
import { JSX } from 'react';

import { Text } from '@videoblocks/design-system';

import { useVoiceoverContext } from '../_context/VoiceoverContext';
import { ContributorVoiceCard } from './ContributorVoiceCard';

/** Only PVC voices with matching assets are rendered — unknown names are skipped */
const VOICES_TO_ASSETS_MAP: Record<string, { imageUrl: string; imageAltText: string }> = {
    'Beth Yap': {
        imageUrl: '/assets/voiceoverAvatars/studio_voice_beth_yap_2x.webp',
        imageAltText: 'Beth Yap',
    },
    'Jas Cole': {
        imageUrl: '/assets/voiceoverAvatars/studio_voice_jas_cole_2x.webp',
        imageAltText: 'Jas Cole',
    },
    'Jayson Brown': {
        imageUrl: '/assets/voiceoverAvatars/studio_voice_jayson_brown_2x.webp',
        imageAltText: 'Jayson Brown',
    },
    'Marshall Usinger': {
        imageUrl: '/assets/voiceoverAvatars/studio_voice_marshall_usinger_2x.webp',
        imageAltText: 'Marshall Usinger',
    },
    'Melody Hong': {
        imageUrl: '/assets/voiceoverAvatars/studio_voice_melody_hong_2x.webp',
        imageAltText: 'Melody Hong',
    },
};

export interface ContributorVoiceListProps {
    selectedVoice: Voice['voice_id'] | null;
    setSelectedVoice: (voiceId: Voice['voice_id']) => void;
    playingVoiceId: Voice['voice_id'] | null;
    setPlayingVoiceId: (voiceId: Voice['voice_id'] | null) => void;
}

export const ContributorVoiceList = ({
    selectedVoice,
    setSelectedVoice,
    playingVoiceId,
    setPlayingVoiceId,
}: ContributorVoiceListProps): JSX.Element | null => {
    const { contributorVoices } = useVoiceoverContext();

    if (contributorVoices.length === 0) {
        return null;
    }

    return (
        <section
            id="contributor-voice-list"
            className="flex flex-col gap-y-6 mt-[88px] scroll-mt-[150px] lg:scroll-mt-[86px]"
        >
            <div className="flex flex-col lg:flex-row lg:justify-between gap-x-2">
                <Text fontSize="lg" fontWeight="semibold" asChild>
                    <h2>Introducing Artist Voices</h2>
                </Text>
                <Text fontSize="sm" fontWeight="regular">
                    Generate authentic voiceovers with Artist voices, created in partnership with our global community
                    of contributors.
                </Text>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                {contributorVoices.map((voice) => {
                    const assets = VOICES_TO_ASSETS_MAP[voice.name];
                    if (!assets) return null;

                    return (
                        <ContributorVoiceCard
                            key={voice.voice_id}
                            voice={voice}
                            selectedVoice={selectedVoice}
                            setSelectedVoice={setSelectedVoice}
                            playingVoiceId={playingVoiceId}
                            setPlayingVoiceId={setPlayingVoiceId}
                            imageUrl={assets.imageUrl}
                            imageAltText={assets.imageAltText}
                        />
                    );
                })}
            </div>
        </section>
    );
};
